import type { ReactNode } from 'react';
import { Card } from '@/components/ui/Card';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { PARTNER_OFFERS_STRINGS as S } from '../partner-offers.constants';

interface OffersSectionProps {
  title: string;
  description: string;
  count: number;
  badgeVariant: BadgeVariant;
  children: ReactNode;
  isEmpty: boolean;
}

/**
 * Shared chrome for the three status-grouped sections on the Perks & Offers
 * console (Awaiting/Accepted/Live) - title, count badge, description,
 * divider, then the section's own rows. Figma node 322:6281 gives each
 * section a slightly different row treatment, so rows are passed as
 * children rather than this component owning row markup (Gate 1: this
 * console has no existing "grouped sections" precedent in the codebase to
 * extend instead).
 */
export function OffersSection({ title, description, count, badgeVariant, children, isEmpty }: OffersSectionProps) {
  return (
    <Card hoverEffect={false}>
      <div className="flex items-center justify-between">
        <h2 className="text-p3 font-bold text-navy">{title}</h2>
        <Badge variant={badgeVariant} size="count">
          {count}
        </Badge>
      </div>
      <p className="mt-2 text-p6 text-slate">{description}</p>
      <div className="my-4 border-t border-divider" />
      {isEmpty ? <EmptyState title={S.SECTION_EMPTY} /> : <div className="flex flex-col gap-3">{children}</div>}
    </Card>
  );
}
